import ee from '../../services/earth-engine';
import { addpSDB } from './index.js';
import { addSPM02, SPM_BAND } from './spm.js';
import { CV_DEFAULTS } from './crossValidation.js';

/**
 * Depth from tree ensembles - Random Forest, Gradient Tree Boost, and a weighted
 * average of the two - fitted inside Earth Engine.
 * The three models the module already had are analytic: each one is a formula with two to four
 * coefficients fitted by least squares, and each one assumes depth responds to reflectance the way
 * its author's equation says it does. A tree ensemble makes no such assumption; it splits the
 * predictors repeatedly on whatever threshold separates the training depths best. What it buys is
 * the ability to fit a different response in clear water than in turbid water within one image, at
 * the cost of needing enough training points to learn it from and of having no coefficients to read
 * or type back in.
 * The feature set, the hyperparameters and their defaults follow the reference Earth Engine script
 * (Mascagni, 2026, sections 1.6.1 and 4.6) rather than being chosen here. Two of its decisions are
 * worth repeating because they are not obvious:
 * - **No logarithms.** A tree splits on a threshold, and the logarithm is monotonic, so a split on
 *   `ln(B)` is the same partition as a split on `B`. Lyzenga's log transform adds nothing to a tree
 *   and only dilutes the random feature draw. Band *ratios* are not monotonic transforms of a
 *   single band and do carry new information, which is why the pSDB pair is in and `lnBand` is out.
 * - **Spatial context.** The mean and standard deviation of a 3x3 neighbourhood stand in for the
 *   tile context a convolutional model would see, and let the trees separate a bright shallow bottom
 *   from a bright turbid patch.
 * Everything runs server-side, in `REGRESSION` output mode so the leaves hold depths rather than
 * classes. Nothing is trained in the browser.
 */

/**
 * @constant RANDOM_FOREST
 * @description Model name for the Random Forest, as `data.modelSelected` carries it.
 * @memberof module:Bathymetry
 */
export const RANDOM_FOREST = 'Random Forest';

/**
 * @constant GRADIENT_BOOST
 * @description Model name for the Gradient Tree Boost.
 * @memberof module:Bathymetry
 */
export const GRADIENT_BOOST = 'Gradient Boost';

/**
 * @constant ENSEMBLE
 * @description Model name for the weighted average of the two.
 * @memberof module:Bathymetry
 */
export const ENSEMBLE = 'RF + GBT Ensemble';

/**
 * @constant TREE_MODELS
 * @description The model names this module answers for.
 * @memberof module:Bathymetry
 */
export const TREE_MODELS = [RANDOM_FOREST, GRADIENT_BOOST, ENSEMBLE];

/**
 * @constant PREDICTOR_SETS
 * @description What the trees are allowed to split on.
 * `FULL` is the reference configuration: every optical band the mission carries, the suspended
 * matter estimate, the two pseudo-bathymetry ratios, and the 3x3 neighbourhood statistics. The two
 * narrower sets exist to isolate where an error comes from, not because they are expected to win.
 * @memberof module:Bathymetry
 */
export const PREDICTOR_SETS = { BANDS: 'bands', RATIOS: 'ratios', FULL: 'full' };

/**
 * @constant SPECTRAL_ROLES
 * @description The optical bands of the reference feature set, by their role in the mission's band
 * map, in the reference's own order. Thermal bands are deliberately absent.
 * @memberof module:Bathymetry
 */
export const SPECTRAL_ROLES = ['aer', 'blue', 'green', 'red', 'nir', 'swir', 'swir2'];

/**
 * @constant RATIO_BANDS
 * @description The pseudo-bathymetry ratios: blue over green, and blue over red.
 * @memberof module:Bathymetry
 */
export const RATIO_BANDS = ['pSDBgreen', 'pSDBred'];

/**
 * @constant CONTEXT_ROLES
 * @description Which bands get neighbourhood statistics. `pSDBgreen` is appended separately because
 * it is derived rather than a mission band.
 * @memberof module:Bathymetry
 */
export const CONTEXT_ROLES = ['blue', 'green', 'red'];

/**
 * @constant CONTEXT_RADIUS_PX
 * @description Neighbourhood radius, in pixels. 1 gives the 3x3 window of the reference, which is
 * 90 m on Landsat.
 * @memberof module:Bathymetry
 */
export const CONTEXT_RADIUS_PX = 1;

/**
 * @constant GBT_LOSSES
 * @description Loss functions Earth Engine accepts for the Gradient Tree Boost. `Huber` is the
 * reference's choice: it is robust to the outliers a bathymetric survey always contains.
 * @memberof module:Bathymetry
 */
export const GBT_LOSSES = ['LeastSquares', 'LeastAbsoluteDeviation', 'Huber'];

/**
 * @constant VARS_PER_SPLIT_MODES
 * @description How many predictors each split may choose from. `P3` is one third of them, the usual
 * rule for regression forests and the reference's choice; `SQRT` is the classification rule.
 * @memberof module:Bathymetry
 */
export const VARS_PER_SPLIT_MODES = ['P3', 'SQRT'];

/**
 * @constant DEFAULT_TREE_PARAMS
 * @description The reference's settings. The two ensembles are tuned separately on purpose: the
 * boost uses a small learning rate, so it needs more trees than the forest to reach the same fit.
 * @memberof module:Bathymetry
 */
export const DEFAULT_TREE_PARAMS = {
  rfNumberOfTrees: 150,
  rfVariablesPerSplit: 'P3',
  rfMinLeafPopulation: 5,
  rfBagFraction: 0.7,
  gbtNumberOfTrees: 300,
  gbtShrinkage: 0.05,
  gbtSamplingRate: 0.7,
  gbtMaxNodes: 32,
  gbtLoss: 'Huber',
  ensembleWeightRf: 0.5,
  predictorSet: PREDICTOR_SETS.FULL,
  seed: 42,
  ...CV_DEFAULTS,
};

/**
 * @constant TREE_PARAM_BOUNDS
 * @description Accepted range of each numeric parameter, so a typed value cannot produce a request
 * Earth Engine refuses or a fit that never returns.
 * @memberof module:Bathymetry
 */
export const TREE_PARAM_BOUNDS = {
  rfNumberOfTrees: { min: 1, max: 500, step: 10 },
  rfMinLeafPopulation: { min: 1, max: 100, step: 1 },
  rfBagFraction: { min: 0.1, max: 1, step: 0.1 },
  gbtNumberOfTrees: { min: 1, max: 1000, step: 50 },
  gbtShrinkage: { min: 0.005, max: 1, step: 0.005 },
  gbtSamplingRate: { min: 0.1, max: 1, step: 0.1 },
  gbtMaxNodes: { min: 2, max: 256, step: 2 },
  ensembleWeightRf: { min: 0, max: 1, step: 0.05 },
  cvFolds: { min: 2, max: 10, step: 1 },
  cvBlockSizeM: { min: 100, max: 20000, step: 100 },
  stratifyBinM: { min: 0.5, max: 10, step: 0.5 },
  samplesPerBin: { min: 50, max: 5000, step: 50 },
};

/**
 * @function isTreeModel
 * @description Whether a model name belongs to this module.
 * @param {String} model - As `data.modelSelected` carries it.
 * **Called by:
 * @see {BathymetryBandsAndAlgorithmSelection} - to show the calibration controls.
 * @see {ProcessingPage} - to pick the fitting branch.
 * @memberof module:Bathymetry
 * @returns {Boolean}
 */
export const isTreeModel = (model) => TREE_MODELS.indexOf(model) !== -1;

/**
 * @function clampTreeParam
 * @description Reads a typed parameter back as a number inside its accepted range, falling back to
 * the reference value when the field is left empty or unreadable.
 * @param {String} name - Parameter name, as {@link TREE_PARAM_BOUNDS} keys it.
 * @param {*} value - The typed value.
 * **Called by:
 * @see {BathymetryBandsAndAlgorithmSelection} - on each change.
 * @memberof module:Bathymetry
 * @returns {Number}
 */
export const clampTreeParam = (name, value, { partial = false } = {}) => {
  const bounds = TREE_PARAM_BOUNDS[name];
  const fallback = DEFAULT_TREE_PARAMS[name];
  if (!bounds) return value;
  if (value === '' || value === null || value === undefined) return partial ? value : fallback;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return partial ? value : fallback;
  /**
   * While typing, only the upper bound is enforced. Raising a half-typed number to the minimum
   * makes a field unreachable: with a minimum of 100, the first keystroke of "300" became 100, the
   * second 1000 and the third 10000, so 300 could not be entered at all. A number still being
   * typed is incomplete rather than invalid, and {@link treeParamsOf} applies the minimum where
   * the value is read.
   */
  return partial ? Math.min(bounds.max, parsed) : Math.min(bounds.max, Math.max(bounds.min, parsed));
};

/**
 * @function treeParamsOf
 * @description Fills in whatever a stored set of parameters is missing, so a session saved before a
 * parameter existed still runs.
 * @param {Object} [params] - Stored parameters.
 * **Called by:
 * @see {ProcessingPage}
 * @see {BathymetryBandsAndAlgorithmSelection}
 * @memberof module:Bathymetry
 * @returns {Object} A complete parameter set.
 */
export const treeParamsOf = (params) => {
  const merged = { ...DEFAULT_TREE_PARAMS, ...(params || {}) };
  /**
   * The bounds are applied here, where the values are read, and not only where they are typed. A
   * field being edited may hold a half-typed number below its minimum, and this is what guarantees
   * that no such value reaches a model - see {@link clampTreeParam}.
   */
  Object.keys(TREE_PARAM_BOUNDS).forEach((name) => {
    merged[name] = clampTreeParam(name, merged[name]);
  });
  return merged;
};

/**
 * @function spectralBandsOf
 * @description The mission bands of the reference feature set that this mission actually carries.
 * A sensor without a coastal/aerosol band simply contributes one predictor fewer rather than
 * failing.
 * @param {Object} missionBands - The mission's band map, as `missionInfo.bands`.
 * **Called by:
 * @see {predictorNames}
 * @memberof module:Bathymetry
 * @returns {Array<String>}
 */
export const spectralBandsOf = (missionBands) =>
  SPECTRAL_ROLES.map((role) => missionBands?.[role]).filter(Boolean);

/**
 * @function contextSourcesOf
 * @description Which bands get neighbourhood statistics: the visible bands plus the green ratio.
 * @param {Object} missionBands - The mission's band map.
 * **Called by:
 * @see {predictorNames}
 * @see {predictorImage}
 * @memberof module:Bathymetry
 * @returns {Array<String>}
 */
export const contextSourcesOf = (missionBands) =>
  [...CONTEXT_ROLES.map((role) => missionBands?.[role]).filter(Boolean), 'pSDBgreen'];

/**
 * @function predictorNames
 * @description The band names the trees are allowed to split on, in a fixed order so that a model
 * trained now can be applied later against the same list.
 * @param {Object} options
 * @param {Array<String>} options.bands - The bands chosen in the band step, `[blue, green, red]`.
 * @param {Object} options.missionBands - The mission's band map.
 * @param {String} [options.predictorSet] - One of {@link PREDICTOR_SETS}.
 * **Called by:
 * @see {trainTreeModel}
 * @see {ProcessingPage} - to report which predictors were used.
 * @memberof module:Bathymetry
 * @returns {Array<String>}
 */
export const predictorNames = ({ bands, missionBands, predictorSet = DEFAULT_TREE_PARAMS.predictorSet }) => {
  if (predictorSet === PREDICTOR_SETS.BANDS) return [...bands];
  if (predictorSet === PREDICTOR_SETS.RATIOS) return [...bands, ...RATIO_BANDS];

  const spectral = spectralBandsOf(missionBands);
  const context = contextSourcesOf(missionBands).reduce(
    (names, band) => [...names, `${band}_mean`, `${band}_stdDev`],
    [],
  );
  return [...spectral, SPM_BAND, ...RATIO_BANDS, ...context];
};

/**
 * @function predictorImage
 * @description Builds the image the trees read, carrying one band per predictor.
 * The ratio bands are masked where either reflectance is not positive, exactly as the Stumpf and
 * Caballero models already are, so a depth map built on them has their coverage.
 * The neighbourhood statistics are computed after an explicit reprojection: a composite has no
 * native projection of its own, and without pinning the grid first the window would be measured in
 * whatever projection the request happens to run in.
 * @param {Object} image - The working image, as an `ee.Image`.
 * @param {Object} options
 * @param {Array<String>} options.bands - The bands chosen in the band step.
 * @param {Object} options.missionBands - The mission's band map.
 * @param {String} [options.predictorSet] - One of {@link PREDICTOR_SETS}.
 * @param {Number} options.scale - Pixel scale of the analysis grid, in metres.
 * @param {String} options.crs - CRS of the analysis grid.
 * **Called by:
 * @see {BathymetryBandsAndAlgorithmSelection} - sampled at the training points.
 * @see {applyTreeModel}
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.Image`.
 */
export const predictorImage = (
  image,
  { bands, missionBands, predictorSet = DEFAULT_TREE_PARAMS.predictorSet, scale, crs },
) => {
  const source = ee.Image(image);

  if (predictorSet === PREDICTOR_SETS.BANDS) return source.select(bands).toFloat();

  const green = addpSDB(source, [bands[0], bands[1]], 'pSDBgreen');
  const red = addpSDB(source, [bands[0], bands[2]], 'pSDBred');

  if (predictorSet === PREDICTOR_SETS.RATIOS) {
    return source.select(bands).addBands([green, red], null, true).toFloat();
  }

  const withSpm = addSPM02(source, missionBands);
  const base = withSpm
    .select([...spectralBandsOf(missionBands), SPM_BAND])
    .addBands([green, red], null, true)
    .toFloat();

  /**
   * A composite carries no projection of its own, and a kernel measured in pixels has nothing to
   * measure against without one. `setDefaultProjection` declares the grid; `reproject` would also
   * pin every upstream computation to it, which on a thirteen-image composite exhausted Earth
   * Engine's memory. The two agree wherever the result is requested at this same scale, which is
   * the only scale this module samples and applies at.
   */
  const context = base
    .select(contextSourcesOf(missionBands))
    .setDefaultProjection({ crs, scale })
    .reduceNeighborhood({
      reducer: ee.Reducer.mean().combine({ reducer2: ee.Reducer.stdDev(), sharedInputs: true }),
      kernel: ee.Kernel.square({ radius: CONTEXT_RADIUS_PX, units: 'pixels' }),
      skipMasked: true,
    });

  return base.addBands(context, null, true).toFloat();
};

/**
 * @function variablesPerSplit
 * @description How many predictors each split may choose from, resolved from the mode and the size
 * of the feature set.
 * @param {Number} featureCount - How many predictors there are.
 * @param {String|Number} mode - One of {@link VARS_PER_SPLIT_MODES}, or an explicit count.
 * **Called by:
 * @see {classifierFor}
 * @memberof module:Bathymetry
 * @returns {Number}
 */
export const variablesPerSplit = (featureCount, mode = DEFAULT_TREE_PARAMS.rfVariablesPerSplit) => {
  if (mode === 'P3') return Math.max(1, Math.round(featureCount / 3));
  if (mode === 'SQRT') return Math.max(1, Math.round(Math.sqrt(featureCount)));
  const explicit = Number(mode);
  return Number.isFinite(explicit) && explicit >= 1 ? Math.round(explicit) : Math.max(1, Math.round(featureCount / 3));
};

/**
 * @function classifierFor
 * @description The untrained regressor for one kind of tree ensemble.
 * `setOutputMode('REGRESSION')` is what makes the leaves hold a mean depth instead of a class vote;
 * without it the same classifier would return the nearest training depth as a label.
 * @param {String} kind - {@link RANDOM_FOREST} or {@link GRADIENT_BOOST}.
 * @param {Object} params - A complete parameter set, as {@link treeParamsOf} returns it.
 * @param {Number} featureCount - How many predictors the fit will see.
 * **Called by:
 * @see {trainTreeModel}
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.Classifier` in regression mode.
 */
export const classifierFor = (kind, params, featureCount) => {
  const classifier =
    kind === GRADIENT_BOOST
      ? ee.Classifier.smileGradientTreeBoost({
          numberOfTrees: params.gbtNumberOfTrees,
          shrinkage: params.gbtShrinkage,
          samplingRate: params.gbtSamplingRate,
          maxNodes: params.gbtMaxNodes,
          loss: params.gbtLoss,
          seed: params.seed,
        })
      : ee.Classifier.smileRandomForest({
          numberOfTrees: params.rfNumberOfTrees,
          variablesPerSplit: variablesPerSplit(featureCount, params.rfVariablesPerSplit),
          minLeafPopulation: params.rfMinLeafPopulation,
          bagFraction: params.rfBagFraction,
          seed: params.seed,
        });
  return classifier.setOutputMode('REGRESSION');
};

/**
 * @function kindsFor
 * @description Which ensembles a model name trains. The ensemble trains both and averages later.
 * @param {String} model - As `data.modelSelected` carries it.
 * **Called by:
 * @see {trainTreeModel}
 * @memberof module:Bathymetry
 * @returns {Array<String>}
 */
export const kindsFor = (model) => (model === ENSEMBLE ? [RANDOM_FOREST, GRADIENT_BOOST] : [model]);

/**
 * @function trainTreeModel
 * @description Trains one or both ensembles on the sampled training points.
 * @param {Object} samples - Points carrying every predictor and the observed depth, as an
 *  `ee.FeatureCollection`.
 * @param {Object} options
 * @param {String} options.model - One of {@link TREE_MODELS}.
 * @param {Array<String>} options.predictors - As {@link predictorNames} returns them.
 * @param {Object} options.params - A complete parameter set.
 * @param {String} [options.label] - Column holding the observed depth.
 * **Called by:
 * @see {ProcessingPage}
 * @memberof module:Bathymetry
 * @returns {Array<Object>} One trained `ee.Classifier` per ensemble.
 */
export const trainTreeModel = (samples, { model, predictors, params, label = 'or_depth' }) => {
  const features = ee.FeatureCollection(samples).filter(ee.Filter.notNull([...predictors, label]));
  return kindsFor(model).map((kind) =>
    classifierFor(kind, params, predictors.length).train({
      features,
      classProperty: label,
      inputProperties: predictors,
    }),
  );
};

/**
 * @function applyTreeModel
 * @description Predicts depth over the whole image with the trained ensembles.
 * The two members are combined by the weight the user holds, not by a plain mean: the reference
 * estimates that weight from out-of-fold predictions, and until the cross-validation exists here it
 * is a value to carry over from there rather than one to assume.
 * Returns the same bands the analytic models do - the predictors, `pred_depth` and
 * `pred_depth_raw` - plus each member's own prediction, so a disagreement between them can be read.
 * @param {Object} image - The working image, as an `ee.Image`.
 * @param {Object} options
 * @param {Array<String>} options.bands - The bands chosen in the band step.
 * @param {Object} options.missionBands - The mission's band map.
 * @param {Object} options.params - A complete parameter set.
 * @param {Number} options.scale - Pixel scale of the analysis grid, in metres.
 * @param {String} options.crs - CRS of the analysis grid.
 * @param {Array<Object>} options.classifiers - As {@link trainTreeModel} returns them.
 * **Called by:
 * @see {ProcessingPage}
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.Image` carrying `pred_depth`.
 */
export const applyTreeModel = (image, { bands, missionBands, params, scale, crs, classifiers }) => {
  const predictors = predictorImage(image, {
    bands,
    missionBands,
    predictorSet: params.predictorSet,
    scale,
    crs,
  });
  const members = classifiers.map((classifier, index) =>
    predictors.classify(classifier).rename(index === 0 ? 'pred_rf' : 'pred_gbt').toFloat(),
  );

  let depth;
  if (members.length > 1) {
    const weight = ee.Number(params.ensembleWeightRf);
    depth = members[0].multiply(weight).add(members[1].multiply(ee.Number(1).subtract(weight)));
  } else {
    depth = members[0];
  }
  depth = depth.rename('pred_depth').toFloat();

  return predictors
    .addBands(members, null, true)
    .addBands([depth.rename('pred_depth_raw'), depth], null, true);
};

/**
 * @function treeMetricsReport
 * @description What to put in the metrics report in place of coefficients, which a forest does not
 * have. Reports the calibration that produced the numbers, so a run can be told apart from the next
 * one and repeated.
 * @param {String} model - One of {@link TREE_MODELS}.
 * @param {Array<String>} predictors - As {@link predictorNames} returns them.
 * @param {Object} params - A complete parameter set.
 * **Called by:
 * @see {ProcessingPage}
 * @memberof module:Bathymetry
 * @returns {Object} Fields for the metrics report.
 */
export const treeMetricsReport = (model, predictors, params) => ({
  TREE_ENSEMBLES: kindsFor(model).join(' + '),
  TREE_PREDICTOR_SET: params.predictorSet,
  TREE_PREDICTOR_COUNT: predictors.length,
  TREE_PREDICTORS: predictors.join(' '),
  ...(model !== GRADIENT_BOOST && {
    TREE_RF_N_TREES: params.rfNumberOfTrees,
    TREE_RF_VARS_PER_SPLIT: variablesPerSplit(predictors.length, params.rfVariablesPerSplit),
    TREE_RF_MIN_LEAF: params.rfMinLeafPopulation,
    TREE_RF_BAG_FRACTION: params.rfBagFraction,
  }),
  ...(model !== RANDOM_FOREST && {
    TREE_GBT_N_TREES: params.gbtNumberOfTrees,
    TREE_GBT_SHRINKAGE: params.gbtShrinkage,
    TREE_GBT_SAMPLING_RATE: params.gbtSamplingRate,
    TREE_GBT_MAX_NODES: params.gbtMaxNodes,
    TREE_GBT_LOSS: params.gbtLoss,
  }),
  ...(model === ENSEMBLE && { TREE_ENSEMBLE_W_RF: params.ensembleWeightRf }),
});
